import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import type { MouseEvent, RefObject, SyntheticEvent } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import I18n from '../i18n/I18n';
import { H } from '../Layout';
import { useSavedFiltersContext } from '../list/savedFilters';
import { reorderByDragEnd } from '../utils/reorderByDragEnd';
import Confirm from './Confirm';
import NewTabCta from './NewTabCta';
import SortablePinnedFilterTab from './SortablePinnedFilterTab';

const ALL_TAB_VALUE = 'all';

type PinnedSavedFilterTabsProps = {
    isSavedFilterChangeRef?: RefObject<boolean>;
};

export default function PinnedSavedFilterTabs({ isSavedFilterChangeRef }: PinnedSavedFilterTabsProps) {
    const {
        state: { pinnedSavedFilters, appliedSavedFilter, showNewTabCta, newTabDisabledReason, canCreateNewTab },
        actions: {
            applySavedFilter,
            clearAppliedSavedFilter,
            openNewTabSaveModal,
            reorderPinnedTabs,
            unpinSavedFilterTab
        }
    } = useSavedFiltersContext();

    const [pendingUnpinId, setPendingUnpinId] = useState<string | null>(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const selectedTab = useMemo(() => {
        const appliedId = appliedSavedFilter?.id;
        if (appliedId && pinnedSavedFilters.some((filter) => filter.id === appliedId)) {
            return appliedId;
        }
        return ALL_TAB_VALUE;
    }, [appliedSavedFilter?.id, pinnedSavedFilters]);

    const pinnedTabIds = useMemo(
        () => pinnedSavedFilters.map((filter) => filter.id).filter((id): id is string => Boolean(id)),
        [pinnedSavedFilters]
    );

    const newTabTooltipToken = useMemo(() => {
        if (!showNewTabCta || canCreateNewTab) {
            return null;
        }
        if (newTabDisabledReason === 'noFilter') {
            return 'filter.savedFilter.newTab.tooltip.noFilter';
        }
        if (newTabDisabledReason === 'alreadyPinned') {
            return 'filter.savedFilter.newTab.tooltip.alreadyPinned';
        }
        return null;
    }, [showNewTabCta, canCreateNewTab, newTabDisabledReason]);

    const handleTabChange = useCallback(
        (_event: SyntheticEvent, nextValue: string) => {
            if (isSavedFilterChangeRef) {
                isSavedFilterChangeRef.current = true;
            }

            if (nextValue === ALL_TAB_VALUE) {
                clearAppliedSavedFilter();
                return;
            }

            applySavedFilter(nextValue);
        },
        [applySavedFilter, clearAppliedSavedFilter, isSavedFilterChangeRef]
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const reorderedIds = reorderByDragEnd(event, pinnedTabIds, pinnedTabIds);
            if (!reorderedIds) {
                return;
            }
            void reorderPinnedTabs(reorderedIds);
        },
        [pinnedTabIds, reorderPinnedTabs]
    );

    const handleUnpinRequest = useCallback((preferenceId: string) => {
        setPendingUnpinId(preferenceId);
    }, []);

    const handleCancelUnpin = useCallback((_event: MouseEvent<HTMLButtonElement>) => {
        setPendingUnpinId(null);
    }, []);

    const handleConfirmUnpin = useCallback(
        (_event: MouseEvent<HTMLButtonElement>) => {
            if (!pendingUnpinId) {
                return;
            }

            if (isSavedFilterChangeRef && appliedSavedFilter?.id === pendingUnpinId) {
                isSavedFilterChangeRef.current = true;
            }

            const preferenceId = pendingUnpinId;
            setPendingUnpinId(null);
            void unpinSavedFilterTab(preferenceId);
        },
        [appliedSavedFilter?.id, isSavedFilterChangeRef, pendingUnpinId, unpinSavedFilterTab]
    );

    const tabsSx = useMemo(
        () => ({
            minHeight: 36,
            borderColor: 'palette.divider',
            width: showNewTabCta ? 'auto' : '100%',
            flex: showNewTabCta ? '0 0 auto' : '1 1 auto',
            minWidth: showNewTabCta ? undefined : 0,
            ...(canCreateNewTab && {
                '& .MuiTabs-indicator': {
                    backgroundColor: 'action.disabled'
                }
            }),
            '& .MuiTab-root': {
                minHeight: 36,
                width: 160,
                minWidth: 160,
                maxWidth: 160,
                px: 1.5,
                fontSize: 'button.fontSize',
                textTransform: 'uppercase' as const,
                overflow: 'hidden',
                '&.Mui-selected': {
                    color: 'primary.main'
                },
                '&.Mui-selected.Mui-disabled': {
                    color: 'action.disabled'
                }
            },
            '& .MuiTabs-scrollButtons': {
                width: 40,
                '&.Mui-disabled': {
                    display: 'none'
                },
                '& .MuiSvgIcon-root': {
                    fontSize: 24,
                    width: 24,
                    height: 24
                }
            }
        }),
        [canCreateNewTab, showNewTabCta]
    );

    return (
        <Box sx={{ width: '100%' }}>
            <H sx={{ width: '100%', alignItems: 'stretch' }}>
                <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                    <SortableContext items={pinnedTabIds} strategy={horizontalListSortingStrategy}>
                        <Tabs
                            value={selectedTab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            allowScrollButtonsMobile
                            scrollButtons="auto"
                            sx={tabsSx}
                        >
                            <Tab
                                key={ALL_TAB_VALUE}
                                value={ALL_TAB_VALUE}
                                data-testid="pinned-filter-tab-all"
                                disabled={canCreateNewTab}
                                label={<I18n token="common.all" />}
                            />
                            {pinnedSavedFilters.map((filter) =>
                                filter.id ? (
                                    <SortablePinnedFilterTab
                                        key={filter.id}
                                        value={filter.id}
                                        label={filter.name}
                                        onUnpin={handleUnpinRequest}
                                    />
                                ) : null
                            )}
                        </Tabs>
                    </SortableContext>
                </DndContext>
                {showNewTabCta && (
                    <NewTabCta
                        disabled={!canCreateNewTab}
                        tooltipToken={newTabTooltipToken}
                        onClick={openNewTabSaveModal}
                    />
                )}
            </H>
            <Confirm
                open={Boolean(pendingUnpinId)}
                title={<I18n token="filter.savedFilter.unpinTab.confirm.title" />}
                body={
                    <Typography>
                        <I18n token="filter.savedFilter.unpinTab.confirm.message" />
                    </Typography>
                }
                okLabelI18n="common.confirm"
                cancelLabelI18n="dialog.cancel"
                onConfirm={handleConfirmUnpin}
                onCancel={handleCancelUnpin}
            />
        </Box>
    );
}
